import { Box, Typography } from "@mui/material";
import { MainContext } from "../../../../main/context/MainContextProvider";
import { useContext } from "react";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export type AnaliseConsolidadaDisponibilidadeProps = {
    sistemaPercentage: number;
    sistemaMeta: number;
    inversoresPercentage: number;
    inversoresMeta: number;
    contratualPercentage: number;
    contratualMeta: number;
    eventosCriticos: number;
    sistemaTarget: number;
    inversoresTarget: number;
    contratualTarget: number;
};

type StatusInfo = {
    label: string;
    color: string;
    icon: React.ReactNode;
};

export default function AnaliseConsolidadaDisponibilidade({
    sistemaPercentage,
    inversoresPercentage,
    contratualPercentage,
    eventosCriticos,
    sistemaMeta,
    inversoresMeta,
    contratualMeta
}: AnaliseConsolidadaDisponibilidadeProps) {
    const { theme } = useContext(MainContext);

    const getStatus = (value: number, target: number): StatusInfo => {
        const valuePercent = value * 100;
        const targetPercent = target;
        const diff = valuePercent - targetPercent;

        if (diff < -5) {
            return {
                label: "Abaixo da meta",
                color: "#EF4444",
                icon: <span style={{ fontSize: "16px", marginRight: "4px", color: "#EF4444" }}>🔴</span>
            };
        }
        // Atenção/Próximo da meta: Dentro da margem de tolerância (2% a 5% abaixo da meta)
        else if (diff >= -5 && diff < 0) {
            return {
                label: "Atenção/Próximo da meta",
                color: "#F59E0B",
                icon: <span style={{ fontSize: "16px", marginRight: "4px", color: "#F59E0B" }}>🟡</span>
            };
        }
        // Dentro da meta / Atende SLA: Valor igual ou superior à meta
        else {
            return {
                label: "Atende SLA",
                color: "#10B981",
                icon: <span style={{ fontSize: "16px", marginRight: "4px", color: "#10B981" }}>🟢</span>
            };
        }
    };

    const sistemaStatus = getStatus(sistemaPercentage, sistemaMeta * 100);
    const inversoresStatus = getStatus(inversoresPercentage, inversoresMeta * 100);
    const contratualStatus = getStatus(contratualPercentage, contratualMeta * 100);



    return (
        <Box
            sx={{
                backgroundColor: "#1E3A5F",
                borderRadius: "8px",
                border: "1px solid #2D5A8C",
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                marginTop: "8px",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                <InfoOutlinedIcon sx={{ fontSize: 20, color: "#60A5FA" }} />
                <Typography
                    sx={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#E5E7EB",
                    }}
                >
                    Análise Consolidada de Disponibilidade
                </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: "600", color: "#E5E7EB" }}>
                        Sistema:
                    </Typography>
                    <Typography sx={{ fontSize: "13px", fontWeight: "600", color: "#E5E7EB" }}>
                        {(sistemaPercentage * 100).toFixed(1)}%
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        {sistemaStatus.icon}
                        <Typography
                            sx={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: sistemaStatus.color,
                            }}
                        >
                            {sistemaStatus.label}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: "600", color: "#E5E7EB" }}>
                        Inversores:
                    </Typography>
                    <Typography sx={{ fontSize: "13px", fontWeight: "600", color: "#E5E7EB" }}>
                        {(inversoresPercentage * 100).toFixed(1)}%
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        {inversoresStatus.icon}
                        <Typography
                            sx={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: inversoresStatus.color,
                            }}
                        >
                            {inversoresStatus.label}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: "600", color: "#E5E7EB" }}>
                        Contratual:
                    </Typography>
                    <Typography sx={{ fontSize: "13px", fontWeight: "600", color: "#E5E7EB" }}>
                        {(contratualPercentage * 100).toFixed(1)}%
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        {contratualStatus.icon}
                        <Typography
                            sx={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: contratualStatus.color,
                            }}
                        >
                            {contratualStatus.label}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    marginTop: "8px",
                    paddingTop: "12px",
                    borderTop: `1px solid ${theme.cardBorderColor}`,
                }}
            >
                <Typography
                    sx={{
                        fontSize: "12px",
                        lineHeight: "1.5",
                        textAlign: "left"
                    }}
                >
                    Total de <strong>{eventosCriticos} eventos críticos </strong>registrados no período. Recomenda-se análise detalhada e ações corretivas.
                </Typography>
            </Box>
        </Box>
    );
}
